import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { Invoice } from "types/Models/invoice";
import { MappedInvoiceResponse } from "store/apis/transactions";

export const InvoiceCategoryDetails = ({
  linkedInvoices,
}: {
  linkedInvoices: MappedInvoiceResponse;
}) => {
  const { link } = useConstructInternalLink();
  const {
    invoice_discount_sum,
    invoices,
    invoice_tax_sum,
    adjustments,
    partial_payment,
  } = linkedInvoices || {};

  return (
    <span className="t-flex t-gap-4 t-flex-col">
      <p className="t-m-0 t-text-body-sm t-font-medium">Invoices</p>
      <div className="t-flex t-flex-col t-gap-5">
        {invoices?.map((invoice, invoiceIndex) => {
          return (
            <div
              key={invoice.uuid || invoiceIndex}
              className="t-flex t-flex-col t-gap-3"
            >
              <div className="t-text-body-sm t-font-medium t-flex t-gap-1 t-items-center">
                <ConditionalLink
                  className="!t-text-purple"
                  to={link(
                    invoice.financial_record_type === "BILL"
                      ? `/books/bills-and-vendors/bill/${invoice.uuid}`
                      : `/books/invoices-and-customers/${invoice.uuid}`
                  )}
                >
                  <span>{invoice.invoice_number}</span>
                </ConditionalLink>
              </div>

              <div className="">
                <div className="t-grid t-grid-cols-4 t-gap-4 t-text-neutral-40 t-text-caption t-mb-1">
                  <div>Items</div>
                  <div className="t-col-span-2">Category</div>
                  <div className="t-text-right">Amount</div>
                </div>

                <div className="t-flex t-gap-3 t-flex-col">
                  {invoice.invoice_items.map((item) => (
                    <div
                      className="t-grid t-grid-cols-4 t-gap-4 t-text-body-sm"
                      key={item.uuid}
                    >
                      <div>{item.description}</div>
                      <div className="t-col-span-2">{item.category?.name}</div>
                      <div className="t-text-right">
                        <AmountSuperScript
                          currencyCode={invoice.currency}
                          amount={Number(item.amount) || 0}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(Boolean(invoice_tax_sum?.amount) ||
        Boolean(invoice_discount_sum?.amount)) && (
        <div className="t-flex t-flex-col t-gap-3">
          <div className="t-text-neutral-40 t-text-body-sm t-font-medium t-flex t-gap-1 t-items-center">
            <span>Tax & Discount</span>
          </div>
          <div className="t-flex t-gap-3 t-flex-col">
            {Boolean(invoice_tax_sum?.amount) && (
              <div className="t-grid t-grid-cols-4 t-gap-4 t-text-body-sm">
                <div>Tax</div>
                <div className="t-col-span-2">{invoice_tax_sum?.category}</div>
                <div className="t-text-right">
                  <AmountSuperScript
                    amount={Number(invoice_tax_sum?.amount) || 0}
                  />
                </div>
              </div>
            )}

            {Boolean(invoice_discount_sum?.amount) && (
              <div className="t-grid t-grid-cols-4 t-gap-4 t-text-body-sm">
                <div>Discount</div>
                <div className="t-col-span-2">
                  {invoice_discount_sum?.category}
                </div>
                <div className="t-text-right">
                  <AmountSuperScript
                    amount={Number(invoice_discount_sum?.amount) || 0}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {(Boolean(adjustments?.manual_adjustment.amount) ||
        Boolean(adjustments?.fx_data_adjustment.fx_difference)) && (
        <>
          <Divider />
          <p className="t-m-0 t-text-body-sm t-font-medium">Adjustments</p>

          <div className="t-flex t-justify-between t-gap-4 t-text-body-sm">
            <InfoItem label="Category">
              {adjustments?.manual_adjustment.category}
            </InfoItem>

            <InfoItem label="Amount">
              <span className="t-text-right">
                <AmountSuperScript
                  amount={adjustments.manual_adjustment.amount}
                />
              </span>
            </InfoItem>
          </div>
        </>
      )}

      {Boolean(adjustments?.fx_data_adjustment.fx_difference) && (
        <div className="t-flex t-justify-between t-gap-4 t-text-body-sm">
          <InfoItem label="category">
            {adjustments?.fx_data_adjustment.category}
          </InfoItem>

          <InfoItem label="category">
            <AmountSuperScript
              amount={
                Number(adjustments?.fx_data_adjustment.fx_difference) || 0
              }
            />
          </InfoItem>
        </div>
      )}

      {partial_payment.uuid && (
        <>
          <Divider />

          <p className="t-m-0 t-text-body-sm t-font-medium">Partial Payment</p>

          <div className="t-flex t-justify-between t-gap-4 t-text-body-sm">
            <InfoItem label="Category">
              <span className="t-text-body-sm">
                {partial_payment?.category?.name}
              </span>
            </InfoItem>

            <InfoItem label="Amount">
              <span className="t-text-body-sm">
                <AmountSuperScript
                  amount={Number(partial_payment?.amount) || 0}
                />
              </span>
            </InfoItem>
          </div>
        </>
      )}
    </span>
  );
};
